<template>
  <!--
    Los cuatro estados de lo que se carga, decididos en un sitio:
    cargando · error · vacío · con datos.

      <UiLoadState :loading="loading" :error="error" :empty="!rows.length"
                   skeleton="list" @retry="load">
        <CustomerCards :rows="rows" />
      </UiLoadState>

    Lo que hace por la pantalla, y que cada pantalla hacía a su manera:

      · El esqueleto solo si la carga tarda (antiparpadeo, useDelayedLoading):
        lo que llega en menos de 150 ms pasa de nada a los datos, y un
        esqueleto que ya salió se queda al menos 300 ms.
      · Los datos entran con un fundido corto; sin movimiento si el sistema
        pide menos movimiento.
      · Al RECARGAR (un filtro, «Actualizar», volver de guardar) no vuelve al
        esqueleto: deja lo que había, un poco apagado, hasta que llega lo
        nuevo. Una lista que se borra y se redibuja con cada letra del
        buscador es lo menos nativo que hay.
      · Error y vacío con la misma cara en todas las pantallas, con
        «Reintentar» si la pantalla escucha `@retry`.

    El esqueleto se elige por su nombre (`skeleton="list|file|cards|form|text"`,
    con sus medidas en `skeleton-props`) o se da entero en el hueco
    `#skeleton`. El vacío y el error también tienen hueco propio (`#empty`,
    `#error`) para quien necesite un botón de «crear el primero».
  -->
  <div
    class="load-state"
    :class="{ 'load-state--busy': refreshing }"
    :aria-busy="loading ? 'true' : null"
  >
    <Transition name="load-state" mode="out-in">
      <div v-if="phase === 'wait'" key="wait" class="load-state__wait" :style="{ minHeight }" />

      <div v-else-if="phase === 'skeleton'" key="skeleton" class="load-state__skeleton">
        <slot name="skeleton">
          <component :is="skeletonComponent" v-bind="skeletonProps" />
        </slot>
      </div>

      <div v-else-if="phase === 'error'" key="error" class="load-state__error" role="alert">
        <slot name="error" :retry="retry">
          <div class="load-state__panel">
            <svg class="load-state__mark" width="28" height="28" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5" /><path d="M12 16.5h.01" />
            </svg>
            <p class="load-state__title">{{ errorTitle ?? uiLabel('loadFailed') }}</p>
            <p v-if="errorText" class="load-state__description">{{ errorText }}</p>
            <UiButton v-if="onRetry" class="load-state__retry" variant="secondary" @click="retry">
              {{ uiLabel('retry') }}
            </UiButton>
          </div>
        </slot>
      </div>

      <div v-else-if="phase === 'empty'" key="empty" class="load-state__empty">
        <slot name="empty">
          <UiEmpty :title="emptyTitle ?? uiLabel('empty')" :description="emptyDescription" />
        </slot>
      </div>

      <div v-else key="content" class="load-state__content">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

import UiButton from './UiButton.vue'
import UiEmpty from './UiEmpty.vue'
import UiSkeleton from './UiSkeleton.vue'
import UiSkeletonList from './UiSkeletonList.vue'
import UiSkeletonFile from './UiSkeletonFile.vue'
import UiSkeletonCards from './UiSkeletonCards.vue'
import UiSkeletonForm from './UiSkeletonForm.vue'
import { useDelayedLoading, SKELETON_DELAY_MS, SKELETON_MINIMUM_MS } from '../composables/useDelayedLoading'
import { uiLabel } from '../utils/labels'

const SKELETONS = {
  list: UiSkeletonList,
  file: UiSkeletonFile,
  cards: UiSkeletonCards,
  form: UiSkeletonForm,
  text: UiSkeleton,
}

const props = defineProps({
  loading: Boolean,
  // Algo que no es falso = la carga falló. Si es un texto, se enseña como
  // explicación: pásalo ya traducido (`errorMessage(e, t)`). Un objeto de
  // error no se enseña tal cual: su mensaje no está en el idioma de nadie.
  error: { type: [String, Boolean, Object, Error], default: null },
  // No hay nada que enseñar (y no es un error): la lista vino vacía.
  empty: Boolean,
  // list | file | cards | form | text, o el hueco #skeleton.
  skeleton: {
    type: String,
    default: 'text',
    // La lista escrita y no `Object.keys(SKELETONS)`: `defineProps` se saca
    // fuera del componente al compilar y no ve lo declarado aquí.
    validator: value => ['list', 'file', 'cards', 'form', 'text'].includes(value),
  },
  // Las medidas del esqueleto elegido: `{ rows: 8, avatar: true }` para una
  // lista, `{ stats: 4, tabs: 5 }` para una ficha…
  skeletonProps: { type: Object, default: () => ({}) },
  emptyTitle: String,
  emptyDescription: String,
  errorTitle: String,
  // El alto que se guarda mientras aún no toca enseñar el esqueleto, para que
  // lo que hay debajo no suba y baje en esos 150 ms.
  minHeight: { type: String, default: '0' },
  delay: { type: Number, default: SKELETON_DELAY_MS },
  minimum: { type: Number, default: SKELETON_MINIMUM_MS },
  // Al recargar, mantener lo que ya se veía (por defecto) en vez de volver al
  // esqueleto. `false` para una pantalla en la que lo de antes ya no vale
  // nada mientras llega lo nuevo.
  keepContent: { type: Boolean, default: true },
  // Declarado como prop para saber si alguien escucha `@retry`: sin nadie
  // que reintente, el botón no se pinta.
  onRetry: Function,
})

const skeletonComponent = computed(() => SKELETONS[props.skeleton] ?? UiSkeleton)
const skeletonProps = computed(() =>
  props.skeleton === 'text' ? { lines: 4, ...props.skeletonProps } : props.skeletonProps)

const { visible } = useDelayedLoading(() => props.loading, { delay: props.delay, minimum: props.minimum })

// Si ya se enseñaron datos alguna vez. Es lo que separa «la primera carga»
// (esqueleto) de «una recarga» (lo de antes, apagado).
const shown = ref(false)

const phase = computed(() => {
  const waiting = props.loading || visible.value
  if (waiting && !(props.keepContent && shown.value)) return visible.value ? 'skeleton' : 'wait'
  if (props.loading && props.keepContent && shown.value) return 'content'
  if (props.error) return 'error'
  if (props.empty) return 'empty'
  return 'content'
})

watch(phase, (now) => { if (now === 'content') shown.value = true }, { immediate: true })
// Un error o un vacío después de unos datos sí cuentan como «ya no hay nada
// que mantener»: la próxima carga vuelve a enseñar esqueleto.
watch(phase, (now) => { if (now === 'error' || now === 'empty') shown.value = false })

// La recarga apagada solo si de verdad tarda: el mismo umbral del esqueleto.
const refreshing = computed(() => phase.value === 'content' && props.loading && visible.value)

const errorText = computed(() => (typeof props.error === 'string' ? props.error : ''))

function retry() {
  props.onRetry?.()
}
</script>

<style scoped>
.load-state { position: relative; }

/* Lo de antes, mientras llega lo nuevo: apagado y sin recibir toques, que un
   toque sobre una fila que va a cambiar abre la que ya no está. */
.load-state--busy > .load-state__content {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity var(--fast);
}

/* La entrada: un fundido corto, lo que hace una app cuando los datos
   aparecen. La salida es casi instantánea: lo que se va (el esqueleto) no
   tiene que hacerse esperar.

   Solo opacidad, y a propósito: un `transform` convierte la caja en el
   bloque contenedor de todo lo `position: fixed` que lleve dentro —la barra
   de acciones anclada de una ficha (`sticky-actions`), los botones flotantes
   de una pantalla— y durante el fundido saldrían pintados en mitad de la
   página. */
.load-state-enter-active { transition: opacity 180ms ease-out; }
.load-state-leave-active { transition: opacity 80ms linear; }
.load-state-enter-from,
.load-state-leave-to { opacity: 0; }
/* El hueco de antes del umbral está vacío: se va sin esperar, o los datos
   que llegan rápido tardarían 80 ms más en verse. */
.load-state__wait.load-state-leave-active { transition: none; }

@media (prefers-reduced-motion: reduce) {
  .load-state-enter-active,
  .load-state-leave-active { transition: none; }
}

/* El error, con la misma cara que UiEmpty para que no parezca otra app. */
.load-state__panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--space-10) var(--space-4);
}
.load-state__mark { color: var(--color-danger); margin-bottom: var(--space-3); }
.load-state__title { font-weight: 600; }
.load-state__description {
  font-size: var(--text-sm);
  color: var(--color-text-soft);
  margin-top: var(--space-1);
  max-width: 44ch;
}
.load-state__retry { margin-top: var(--space-4); }
</style>
